// utils/calendarService.js

/**
 * Parse date and time strings into Date objects
 */
const parseDateTime = (dateString, timeString) => {
  if (!dateString || !timeString) return new Date();
  
  // Parse date (2026-02-04T00:00:00)
  const date = new Date(dateString);
  
  // Parse time (5:15 PM)
  const timeMatch = timeString.match(/(\d+):(\d+)\s*(AM|PM)/i);
  if (timeMatch) {
    let hours = parseInt(timeMatch[1]);
    const minutes = parseInt(timeMatch[2]);
    const period = timeMatch[3].toUpperCase();
    
    // Convert to 24-hour format
    if (period === 'PM' && hours < 12) {
      hours += 12;
    } else if (period === 'AM' && hours === 12) {
      hours = 0;
    }
    
    date.setHours(hours, minutes, 0, 0);
  }
  
  return date;
};

/**
 * Format date for Google Calendar (YYYYMMDDTHHMMSSZ)
 */
const formatDateForGoogle = (date) => {
  // Create a new date to avoid timezone issues
  const utcDate = new Date(date.getTime());
  return utcDate.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/g, '');
};

/**
 * Generate Google Calendar URL
 */
export const generateGoogleCalendarUrl = (session) => {
  const startDate = parseDateTime(session.scheduledDate, session.scheduledTime);
  const endDate = parseDateTime(session.scheduledDate, session.scheduledEndTime);
  
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: `${session.title || 'Session'} with ${session.tutorName || 'Tutor'}`,
    details: `
    Session Details:

    Tutor: ${session.tutorName || 'N/A'} 
    Subject: ${session.subject || 'N/A'} 
    Meeting Link: ${session.meetingLink || 'Online'}
    `,
    location: session.meetingLink || 'Online',
    dates: `${formatDateForGoogle(startDate)}/${formatDateForGoogle(endDate)}`,
    ctz: Intl.DateTimeFormat().resolvedOptions().timeZone
  });

  console.log("params = ", params);

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

/**
 * Generate Outlook Calendar URL
 */
export const generateOutlookCalendarUrl = (session) => {
  const startDate = parseDateTime(session.scheduledDate, session.scheduledTime);
  const endDate = parseDateTime(session.scheduledDate, session.scheduledEndTime);
  
  const params = new URLSearchParams({
    path: '/calendar/action/compose',
    rru: 'addevent',
    subject: `${session.title || 'Session'} with ${session.tutorName || 'Tutor'}`,
    body: `
    Session Details:
    Tutor: ${session.tutorName || 'N/A'}
    Subject: ${session.subject || 'N/A'}
    Meeting Link: ${session.meetingLink || 'Online'}`,
    location: session.meetingLink || 'Online',
    startdt: startDate.toISOString(),
    enddt: endDate.toISOString()
  });

  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
};

/**
 * Generate Yahoo Calendar URL
 */
export const generateYahooCalendarUrl = (session) => {
  const startDate = parseDateTime(session.scheduledDate, session.scheduledTime);
  const endDate = parseDateTime(session.scheduledDate, session.scheduledEndTime);
  
  // Calculate duration in minutes
  const duration = Math.round((endDate - startDate) / (1000 * 60));
  
  const params = new URLSearchParams({
    title: `${session.title || 'Session'} with ${session.tutorName || 'Tutor'}`,
    desc: `
    Session Details:
    Tutor: ${session.tutorName || 'N/A'}
    Subject: ${session.subject || 'N/A'}
    Meeting Link: ${session.meetingLink || 'Online'}`,
    in_loc: session.meetingLink || 'Online',
    dur: duration.toString(),
    st: formatDateForGoogle(startDate).replace('Z', ''),
    et: formatDateForGoogle(endDate).replace('Z', '')
  });

  return `https://calendar.yahoo.com/?v=60&view=d&type=20&${params.toString()}`;
};

/**
 * Generate iCal format for the event
 */
export const generateICalEvent = (session) => {
  const startDate = parseDateTime(session.scheduledDate, session.scheduledTime);
  const endDate = parseDateTime(session.scheduledDate, session.scheduledEndTime);
  
  // Format dates for iCal
  const formatDateForICal = (date) => {
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');
    const seconds = String(date.getUTCSeconds()).padStart(2, '0');
    return `${year}${month}${day}T${hours}${minutes}${seconds}Z`;
  };

  // Generate unique ID
  const uid = `${session.sessionId || Date.now()}@nkenne.com`;
  
  // Escape special characters for iCal
  const escapeICalText = (text) => {
    if (!text) return '';
    return text
      .replace(/\\/g, '\\\\')
      .replace(/;/g, '\\;')
      .replace(/,/g, '\\,')
      .replace(/\n/g, '\\n');
  };

  // Create iCal content
  const iCalContent = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//NKENNE//Calendar Service//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `SUMMARY:${escapeICalText(session.title || `Session with ${session.tutorName || 'Tutor'}`)}`,
    `DESCRIPTION:${escapeICalText(`
    Session Details:
    Tutor: ${session.tutorName || 'N/A'}
    Subject: ${session.subject || 'N/A'}
    Meeting Link: ${session.meetingLink || 'Online'}`)}`,
    `DTSTART:${formatDateForICal(startDate)}`,
    `DTEND:${formatDateForICal(endDate)}`,
    `DTSTAMP:${formatDateForICal(new Date())}`,
    `LOCATION:${escapeICalText(session.meetingLink || 'Online')}`,
    //`ORGANIZER;CN="${escapeICalText(session.tutorName || 'Tutor')}":mailto:${session.tutorEmail || 'tutor@example.com'}`,
    //`ATTENDEE;CN="${escapeICalText(session.studentName || 'Student')}":mailto:${session.studentEmail || 'student@example.com'}`,
    'END:VEVENT',
    'END:VCALENDAR'
  ].join('\r\n');

  return iCalContent;
};

/**
 * Download iCal file
 */
export const downloadICalFile = (session) => {
  const iCalContent = generateICalEvent(session);
  const blob = new Blob([iCalContent], { type: 'text/calendar;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  
  link.href = url;
  link.download = `session-${session.sessionId || 'event'}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Open calendar in new window/tab
 */
export const addToCalendar = (session, calendarType) => {
  switch (calendarType) {
    case 'google':
      window.open(generateGoogleCalendarUrl(session), '_blank', 'noopener,noreferrer');
      break;
    case 'outlook':
      window.open(generateOutlookCalendarUrl(session), '_blank', 'noopener,noreferrer');
      break;
    case 'yahoo':
      window.open(generateYahooCalendarUrl(session), '_blank', 'noopener,noreferrer');
      break;
    case 'apple':
      // For Apple, show instructions and download iCal
      alert('To add to Apple Calendar:\n1. Click "iCal File" option\n2. Download the .ics file\n3. Open the file - it will automatically open in Apple Calendar\n4. Click "Add All" to save the event');
      downloadICalFile(session);
      break;
    case 'ical':
      downloadICalFile(session);
      break;
    default:
      downloadICalFile(session);
      break;
  }
};